/**
 * `acknowledge-notification` — O-1, FR-01, FR-03, FR-04, §3.
 *
 * Acknowledgement is a **first-class action** (§3.2): recorded with actor and
 * timestamp, and it stops escalation. A single email thirty days out that lands in
 * a spam folder is not a control; the ladder plus acknowledgement is.
 *
 * ## Idempotency
 *
 * A notification is addressed by the tuple `(rule, contract, occurrence_date,
 * tier)` — the same key `notification_sent` is unique on (§3.3), not a surrogate
 * id. Acknowledging by natural key makes the acknowledgement itself idempotent: a
 * double-click, a retried request and a duplicated Teams action card all resolve
 * to the same row, and `was_already_acknowledged` reports which one got there
 * first.
 *
 * This matters more than it looks. Get idempotency wrong and a daily timer
 * re-sends the same alert every morning, people mute the channel, and **O-1 fails
 * silently while every dashboard stays green** (CLAUDE.md rule 15).
 */
import { z } from 'zod';
import { ContractKeySchema, IsoDateSchema, IsoTimestampUtcSchema, PartyKeySchema, UpnSchema, } from '../primitives.js';
import { NotificationTierSchema, NotificationWatchTypeSchema } from '../vocabulary.js';
import { AuditReceiptSchema } from '../provenance.js';
import { PageInfoSchema, PageRequestSchema } from '../pagination.js';
import { TagFilterSchema } from './contract-tags.js';
import { NotificationOccurrenceKeySchema, NotificationSchema, } from '../domain/notification.js';
/* -------------------------------------------------------------------------- */
/* Reading outstanding notifications                                           */
/* -------------------------------------------------------------------------- */
export const NotificationListRequestSchema = z
    .object({
    contract_key: ContractKeySchema.optional(),
    party_key: PartyKeySchema.optional(),
    /** CLM-77 — all-of filter on the contract's own tags. */
    tags: TagFilterSchema.optional(),
    watch_type: z.array(NotificationWatchTypeSchema).min(1).optional(),
    tier: z.array(NotificationTierSchema).min(1).optional(),
    /** Omit to see unacknowledged only — the state that needs action. */
    include_acknowledged: z.boolean().optional(),
    page: PageRequestSchema.optional(),
})
    .strict();
export const NotificationListResponseSchema = z.object({
    notifications: z.array(NotificationSchema),
    page: PageInfoSchema,
    /** For the dashboard. Within the caller's scope, never corpus-wide. */
    totals: z.object({
        unacknowledged: z.number().int().min(0),
        /** Re-firing daily until acknowledged (§3.2). The number that matters. */
        past_due: z.number().int().min(0),
    }),
});
/* -------------------------------------------------------------------------- */
/* Acknowledging                                                               */
/* -------------------------------------------------------------------------- */
export const AcknowledgeNotificationRequestSchema = z
    .object({
    /** The §3.3 idempotency key. */
    occurrence: NotificationOccurrenceKeySchema,
    /**
     * Optional note — *"renewal already discussed with the client, decision by
     * 12 Sept"*. Recorded on the audit row, because the reason an alert was
     * acknowledged is the part a review will ask about.
     */
    note: z.string().min(1).max(1000).optional(),
    /**
     * Acknowledge every open tier for this occurrence, not just the one named.
     *
     * The ladder is cumulative, so a contract past due may have four open rows for
     * one date. Without this, an owner clears the 7-day alert and the 30-day row
     * keeps escalating to the PMO Director — technically correct, and the fastest
     * way to teach people that acknowledgement does not work.
     */
    acknowledge_all_tiers_for_occurrence: z.boolean().optional(),
})
    .strict();
export const AcknowledgeNotificationResponseSchema = z.object({
    /** Every occurrence row this call acknowledged. */
    acknowledged: z.array(z.object({
        occurrence: NotificationOccurrenceKeySchema,
        acknowledged_by_upn: UpnSchema,
        acknowledged_utc: IsoTimestampUtcSchema,
    })),
    /**
     * True when the row was already acknowledged. **Not** an error: idempotent by
     * design, and the existing acknowledgement is returned unchanged rather than
     * being overwritten with a later actor.
     */
    was_already_acknowledged: z.boolean(),
    /**
     * Structurally `true`. Acknowledgement stopping escalation is the behaviour that
     * makes the ladder a control rather than a mailing list (§3.2); a response that
     * could report `false` would be reporting a broken control.
     */
    escalation_stopped: z.literal(true),
    /**
     * Tiers still open for this occurrence. Non-empty only when
     * `acknowledge_all_tiers_for_occurrence` was not set — so the front end can
     * offer to clear the rest instead of leaving them to re-fire.
     */
    remaining_open_tiers: z.array(NotificationTierSchema),
    /**
     * The date this occurrence warns about, echoed for display. The alert is about a
     * *contract* date, not about when the email was sent, and conflating the two is
     * the most common way notification UIs mislead.
     */
    occurrence_date: IsoDateSchema,
    audit: AuditReceiptSchema,
});
//# sourceMappingURL=acknowledge-notification.js.map